<template>
  <div class="fts-history-text-diff">
    <div class="hd-toggle" role="tablist">
      <button
        type="button"
        class="hd-tab"
        :class="{ 'hd-tab--active': mode === 'diff' }"
        @click="mode = 'diff'"
      >Diff</button>
      <button
        type="button"
        class="hd-tab"
        :class="{ 'hd-tab--active': mode === 'original' }"
        @click="mode = 'original'"
      >Original</button>
      <button
        type="button"
        class="hd-tab"
        :class="{ 'hd-tab--active': mode === 'new' }"
        @click="mode = 'new'"
      >New</button>
    </div>

    <div class="hd-body"><template v-if="mode === 'diff'"><span v-for="(t, i) in diffTokens" :key="i" :class="tokenClass(t)">{{ t.text }}</span></template><template v-else-if="mode === 'original'">{{ original }}</template><template v-else>{{ current }}</template></div>
  </div>
</template>

<script>
// Word-level diff: tokenise into word / whitespace runs, LCS-align, and emit
// same / add / remove tokens. Whitespace is kept so line breaks and spacing
// survive; only word tokens get coloured (see tokenClass).
function tokenize(text) {
  return (typeof text === 'string' ? text : '').match(/\s+|\S+/g) || [];
}

function computeWordDiff(a, b) {
  const A = tokenize(a);
  const B = tokenize(b);

  // Guard: word LCS is O(n*m); for very large texts fall back to whole-block
  // add/remove rather than risk a heavy diff.
  if (A.length > 1200 || B.length > 1200) {
    const out = [];
    if (a) out.push({ type: 'remove', text: a });
    if (b) out.push({ type: 'add', text: b });
    return out;
  }

  const n = A.length;
  const m = B.length;
  const dp = [];
  for (let i = 0; i <= n; i++) dp.push(new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { out.push({ type: 'same', text: A[i] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { out.push({ type: 'remove', text: A[i] }); i++; }
    else { out.push({ type: 'add', text: B[j] }); j++; }
  }
  while (i < n) { out.push({ type: 'remove', text: A[i] }); i++; }
  while (j < m) { out.push({ type: 'add', text: B[j] }); j++; }
  return out;
}

export default {
  name: "fts-history-text-diff",
  props: {
    original: { type: String, default: '' },
    current: { type: String, default: '' },
  },
  data: () => ({
    mode: 'diff',
  }),
  computed: {
    diffTokens() {
      return computeWordDiff(this.original, this.current);
    }
  },
  methods: {
    tokenClass(token) {
      if (/^\s+$/.test(token.text)) return ''; // whitespace stays neutral
      if (token.type === 'add') return 'hd-add';
      if (token.type === 'remove') return 'hd-remove';
      return '';
    }
  }
}
</script>

<style lang="scss" scoped>
.fts-history-text-diff {
  .hd-toggle {
    display: inline-flex;
    margin-bottom: 5px;
    border: 1px solid var(--inistate-border-primary);
    border-radius: 5px;
    overflow: hidden;
  }
  .hd-tab {
    padding: 1px 9px;
    background: none;
    border: 0;
    border-right: 1px solid var(--inistate-border-primary);
    font: inherit;
    font-size: 0.7rem;
    line-height: 1.4;
    color: var(--inistate-text-tertiary);
    cursor: pointer;
    &:last-child { border-right: 0; }
    &:hover:not(.hd-tab--active) { color: var(--inistate-text-primary); }
    &.hd-tab--active {
      background-color: var(--inistate-background-secondary);
      color: var(--inistate-text-primary);
      font-weight: 500;
    }
    &:focus-visible { outline: 2px solid var(--fts-primary); outline-offset: -2px; }
  }

  .hd-body {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }
  .hd-add {
    color: var(--fts-success);
    background-color: rgba(var(--fts-success-rgb), 0.12);
    border-radius: 0.1rem;
  }
  .hd-remove {
    color: var(--fts-danger);
    background-color: rgba(var(--fts-danger-rgb), 0.1);
    border-radius: 0.1rem;
    text-decoration: line-through;
  }
}
</style>
